import * as React from "react";
import { useSelector } from "react-redux";
import { Dialog } from "@progress/kendo-react-dialogs";
import { Form, Field, FormElement } from "@progress/kendo-react-form";
import { Input, Checkbox } from "@progress/kendo-react-inputs";
import { Label } from "@progress/kendo-react-labels";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import SubmitButton from "components/common/SubmitButton";
import { useCompany } from "services/companyService";
import { getAllNominals, useAllNominals } from "services/nominalsService";

const EditCurrency = (props) => {
  const ADD_NEW = props.type === "addNew";
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submitAttempted, setSubmitAttempted] = React.useState(false);
  const { data: company } = useCompany();
  const { data: nominals = [], loading: nominalsLoading } = useAllNominals();
  const companyFetched = useSelector((state) => state.company.fetched);
  const multiCurrencyModuleOn = Boolean(
    company?.modules?.MultiCurrency ?? company?.Modules?.MultiCurrency
  );
  const showMultiCurrencyUi =
    !companyFetched || multiCurrencyModuleOn;
  const useBaseCurrencyOnly =
    companyFetched && !multiCurrencyModuleOn;
  const initialClientControlRef =
    typeof props?.item?.clientControlNominalRef === "object"
      ? props?.item?.clientControlNominalRef?.nominalRef
      : props?.item?.clientControlNominalRef;
  const initialClientControlOption = React.useMemo(
    () =>
      initialClientControlRef
        ? nominals.find((n) => n.nominalRef === initialClientControlRef)
        : null,
    [nominals, initialClientControlRef]
  );

  React.useEffect(() => {
    getAllNominals();
  }, []);

  const nominalCurrencyCode = React.useCallback((n) => {
    if (!n || typeof n !== "object") {
      return "";
    }
    return String(
      n.currencyCode ?? n.CurrencyCode ?? n.currency ?? ""
    ).trim();
  }, []);

  const buildClientControlRows = React.useCallback(
    (nominalsList, formCurrencyCode) => {
      const code = String(formCurrencyCode || "").trim().toUpperCase();
      const base = String(company?.baseCurrency || "").trim().toUpperCase();
      if (!code) {
        return [];
      }
      return nominalsList
        .filter((n) => {
          if (Boolean(n?.archived)) {
            return false;
          }
          if (!Boolean(n?.control)) {
            return false;
          }
          if (n?.active === false || n?.active === 0) {
            return false;
          }
          const nc = nominalCurrencyCode(n).toUpperCase();
          if (nc === code) {
            return true;
          }
          if (!nc && base && code === base) {
            return true;
          }
          return false;
        })
        .map((n) => ({
          ...n,
          display: `${n.nominalRef} - ${n.nominalName}`,
        }));
    },
    [company?.baseCurrency, nominalCurrencyCode]
  );

  const validateForm = (values) => {
    const errors = {};
    if (
      ADD_NEW &&
      (!values.currencyCode || values.currencyCode.trim() === "")
    ) {
      errors.currencyCode = "Currency code is required";
    }

    if (!values.name || values.name.trim() === "") {
      errors.name = "Name is required";
    }

    return errors;
  };

  const handleFormSubmit = async (formData) => {
    setIsSubmitting(true);
    try {
      await props.onSubmit({
        ...formData,
        clientControlNominalRef: useBaseCurrencyOnly
          ? null
          : typeof formData.clientControlNominalRef === "object"
            ? formData.clientControlNominalRef?.nominalRef
            : formData.clientControlNominalRef ?? null,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      title={ADD_NEW ? "Add new Currency" : `Edit ${props?.item?.name}`}
      onClose={props.cancelEdit}
      width={500}
    >
      <Form
        key={ADD_NEW ? "currency-form-add" : `currency-form-edit-${nominalsLoading ? "0" : "1"}`}
        onSubmit={handleFormSubmit}
        initialValues={{
          active: true,
          ...(!ADD_NEW && props.item),
          ...(!ADD_NEW &&
            initialClientControlOption && {
              clientControlNominalRef: {
                ...initialClientControlOption,
                display: `${initialClientControlOption.nominalRef} - ${
                  initialClientControlOption.nominalName || "Client Control"
                }`,
              },
            }),
        }}
        validator={validateForm}
        render={(formRenderProps) => (
          <>
            <div className="p-3 ps-4">
              <FormElement>
                <fieldset className={"k-form-fieldset"}>
                  <div className="row g-2 mb-2">
                    <div className="col-4">
                      <Label className="label mt-1 w-100">
                        Currency Code&nbsp;
                      </Label>
                      <Field
                        className="mt-1 w-100"
                        name={"currencyCode"}
                        component={Input}
                        maxLength={3}
                        disabled={!ADD_NEW}
                        onChange={(e) => {
                          const nextCurrencyCode = e?.value || "";
                          formRenderProps.onChange("currencyCode", {
                            value: nextCurrencyCode,
                          });

                          const selectedClientControl = formRenderProps.valueGetter(
                            "clientControlNominalRef"
                          );
                          const selectedClientControlRef =
                            typeof selectedClientControl === "object"
                              ? selectedClientControl?.nominalRef
                              : selectedClientControl;

                          const isStillValid = nominals.some(
                            (n) =>
                              n?.active !== false &&
                              n?.active !== 0 &&
                              Boolean(n?.control) &&
                              n?.nominalRef === selectedClientControlRef &&
                              (() => {
                                const nc = nominalCurrencyCode(n).toUpperCase();
                                const next = String(nextCurrencyCode || "")
                                  .trim()
                                  .toUpperCase();
                                const base = String(company?.baseCurrency || "")
                                  .trim()
                                  .toUpperCase();
                                return (
                                  nc === next ||
                                  (!nc && base && next === base)
                                );
                              })()
                          );

                          if (!isStillValid) {
                            formRenderProps.onChange("clientControlNominalRef", {
                              value: null,
                            });
                          }
                        }}
                      />
                      {submitAttempted &&
                        formRenderProps.errors?.currencyCode && (
                          <div className="fs-sm my-1 text-danger">
                            <i>{formRenderProps.errors.currencyCode}</i>
                          </div>
                        )}
                    </div>

                    <div className="col-4">
                      <Label className="label mt-1 w-100">Symbol&nbsp;</Label>
                      <Field
                        className="mt-1 w-100"
                        name={"symbol"}
                        component={Input}
                        maxLength={3}
                      />
                    </div>
                  </div>

                  <div className="mb-2 col-8">
                    <Label className="label mt-1 w-100">Name&nbsp;</Label>
                    <Field
                      className="mt-1 w-100"
                      name={"name"}
                      component={Input}
                      maxLength={30}
                    />
                    {submitAttempted && formRenderProps.errors?.name && (
                      <div className="fs-sm my-1 text-danger">
                        <i>{formRenderProps.errors.name}</i>
                      </div>
                    )}
                  </div>

                  {showMultiCurrencyUi && (
                    <div className="mb-3 col-8">
                      <Label className="label mt-1 w-100">Client Control&nbsp;</Label>
                      <Field
                        className="mt-1 w-100"
                        name={"clientControlNominalRef"}
                        component={ComboBox}
                        textField="display"
                        dataItemKey="nominalRef"
                        data={buildClientControlRows(
                          nominals,
                          formRenderProps.valueGetter("currencyCode")
                        )}
                        disabled={!formRenderProps.valueGetter("currencyCode")}
                        onChange={(e) => {
                          formRenderProps.onChange("clientControlNominalRef", {
                            value: e.value,
                          });
                        }}
                      />
                    </div>
                  )}

                  <Field
                    name={"active"}
                    component={Checkbox}
                    label={"Active"}
                  />
                </fieldset>
              </FormElement>
            </div>
            <div className="dialog-footer">
              <button
                className="bgBtnSecondary common-btn me-2"
                onClick={props.cancelEdit}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <SubmitButton
                className="me-3"
                isLoading={isSubmitting}
                onClick={() => {
                  setSubmitAttempted(true);
                  formRenderProps.onSubmit();
                }}
                disabled={!formRenderProps.allowSubmit}
              >
                {ADD_NEW ? "Save" : "Update"}
              </SubmitButton>
            </div>
          </>
        )}
      />
    </Dialog>
  );
};

export default EditCurrency;
